import * as THREE from 'three';
import {
  TERRAIN_WIDTH, TERRAIN_DEPTH,
  TERRAIN_SEGMENTS_X, TERRAIN_SEGMENTS_Z,
  TERRAIN_MAX_HEIGHT,
} from '@use/shared';
import { applyCrater, sampleHeight, sampleNormal, generateTerrainHeightmap } from '@use/shared';
import type { CraterEvent, Vec3 } from '@use/shared';

export class Terrain {
  public mesh: THREE.Mesh;
  public heightmap: Float32Array;
  public geometry: THREE.PlaneGeometry;
  public waterPlane: THREE.Mesh;

  private vertsX: number;
  private vertsZ: number;
  private material: THREE.MeshStandardMaterial;
  private colorAttribute: THREE.BufferAttribute;
  private scene: THREE.Scene;

  constructor(scene: THREE.Scene, seed: number = 42) {
    this.scene = scene;
    this.vertsX = TERRAIN_SEGMENTS_X + 1;
    this.vertsZ = TERRAIN_SEGMENTS_Z + 1;
    this.heightmap = new Float32Array(this.vertsX * this.vertsZ);

    // Generate heightmap from noise
    this.generateHeightmap(seed);

    // Create geometry
    this.geometry = new THREE.PlaneGeometry(
      TERRAIN_WIDTH, TERRAIN_DEPTH,
      TERRAIN_SEGMENTS_X, TERRAIN_SEGMENTS_Z
    );

    // Rotate plane to be horizontal (PlaneGeometry is XY by default)
    this.geometry.rotateX(-Math.PI / 2);

    // Apply heightmap to geometry vertices
    const positions = this.geometry.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < positions.count; i++) {
      positions.setY(i, this.heightmap[i]);
    }
    positions.needsUpdate = true;

    // Compute normals for flat shading
    this.geometry.computeVertexNormals();

    // Vertex coloring based on height + slope + noise
    const colors = new Float32Array(positions.count * 3);
    this.colorAttribute = new THREE.BufferAttribute(colors, 3);
    this.geometry.setAttribute('color', this.colorAttribute);
    this.updateVertexColors();

    // Material — PBR standard material with flat shading for low-poly look
    this.material = new THREE.MeshStandardMaterial({
      vertexColors: true,
      flatShading: true,
      roughness: 0.85,
      metalness: 0.0,
    });

    // Mesh
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.receiveShadow = true;
    this.mesh.castShadow = false;
    scene.add(this.mesh);

    // Water plane at y=0 to fill valleys and crater bottoms
    this.waterPlane = this.createWaterPlane();
    scene.add(this.waterPlane);
  }

  private createWaterPlane(): THREE.Mesh {
    const waterGeo = new THREE.PlaneGeometry(TERRAIN_WIDTH * 1.2, TERRAIN_DEPTH * 1.2);
    waterGeo.rotateX(-Math.PI / 2);
    const waterMat = new THREE.MeshStandardMaterial({
      color: 0x2266aa,
      transparent: true,
      opacity: 0.55,
      roughness: 0.1,
      metalness: 0.2,
      side: THREE.DoubleSide,
    });
    const water = new THREE.Mesh(waterGeo, waterMat);
    water.position.y = -1; // slightly below zero level
    water.receiveShadow = true;
    return water;
  }

  private generateHeightmap(seed: number): void {
    this.heightmap = generateTerrainHeightmap(seed);
  }

  /**
   * Simple seeded hash for per-vertex color noise (deterministic from position).
   */
  private colorNoise(wx: number, wz: number): number {
    // Simple hash — good enough for color variation
    const n = Math.sin(wx * 12.9898 + wz * 78.233) * 43758.5453;
    return n - Math.floor(n); // 0..1
  }

  private updateVertexColors(affectedIndices?: number[]): void {
    const positions = this.geometry.attributes.position as THREE.BufferAttribute;
    const colors = this.colorAttribute;
    const cellW = TERRAIN_WIDTH / TERRAIN_SEGMENTS_X;
    const cellD = TERRAIN_DEPTH / TERRAIN_SEGMENTS_Z;

    const indices = affectedIndices || Array.from({ length: positions.count }, (_, i) => i);

    for (const i of indices) {
      const y = positions.getY(i);
      const normalizedH = y / TERRAIN_MAX_HEIGHT;

      // Get world position for this vertex
      const gx = i % this.vertsX;
      const gz = Math.floor(i / this.vertsX);
      const wx = gx * cellW - TERRAIN_WIDTH / 2;
      const wz = gz * cellD - TERRAIN_DEPTH / 2;

      // Sample slope at this vertex
      const normal = sampleNormal(this.heightmap, wx, wz);
      const slopeAngle = Math.acos(Math.max(0, Math.min(1, normal.y))) * (180 / Math.PI);

      // Color noise for variation (breaks harsh banding)
      const noise = (this.colorNoise(wx * 0.1, wz * 0.1) - 0.5) * 0.08;

      let r: number, g: number, b: number;

      if (y < -3) {
        // Deep crater / underwater — dark scorched earth
        r = 0.12 + noise; g = 0.08 + noise * 0.5; b = 0.06;
      } else if (slopeAngle > 45) {
        // Steep slopes — exposed rock/cliff face regardless of height
        const rockNoise = this.colorNoise(wx * 0.05, wz * 0.07);
        r = 0.42 + rockNoise * 0.08 + noise;
        g = 0.38 + rockNoise * 0.06 + noise;
        b = 0.34 + rockNoise * 0.05;
      } else if (slopeAngle > 30) {
        // Moderate slope — transition from ground cover to rock
        const t = (slopeAngle - 30) / 15; // 0 at 30°, 1 at 45°
        // Blend between ground color and rock color
        const groundR = normalizedH < 0.4 ? 0.22 + noise : 0.45 + noise;
        const groundG = normalizedH < 0.4 ? 0.48 + noise : 0.38 + noise;
        const groundB = normalizedH < 0.4 ? 0.16 : 0.18;
        r = groundR + t * (0.42 - groundR);
        g = groundG + t * (0.38 - groundG);
        b = groundB + t * (0.34 - groundB);
      } else if (normalizedH < 0.05) {
        // Shoreline — wet sand/mud
        r = 0.35 + noise; g = 0.30 + noise; b = 0.20;
      } else if (normalizedH < 0.25) {
        // Low ground — lush dark green
        r = 0.18 + noise; g = 0.48 + noise; b = 0.12;
      } else if (normalizedH < 0.50) {
        // Mid elevation — lighter green to dry grass
        const t = (normalizedH - 0.25) / 0.25;
        r = 0.18 + t * 0.28 + noise;
        g = 0.48 - t * 0.08 + noise;
        b = 0.12 + t * 0.04;
      } else if (normalizedH < 0.72) {
        // Higher — brown/tan dirt and dry rock
        const t = (normalizedH - 0.50) / 0.22;
        r = 0.46 + t * 0.10 + noise;
        g = 0.40 - t * 0.05 + noise;
        b = 0.16 + t * 0.12;
      } else if (normalizedH < 0.88) {
        // Mountain — gray rock
        const t = (normalizedH - 0.72) / 0.16;
        r = 0.50 + t * 0.08 + noise;
        g = 0.48 + t * 0.07 + noise;
        b = 0.44 + t * 0.08;
      } else {
        // Peaks — snow-capped
        const t = Math.min(1, (normalizedH - 0.88) / 0.12);
        r = 0.58 + t * 0.32 + noise;
        g = 0.55 + t * 0.35 + noise;
        b = 0.52 + t * 0.38;
      }

      // Clamp colors
      colors.setXYZ(i, Math.max(0, Math.min(1, r)), Math.max(0, Math.min(1, g)), Math.max(0, Math.min(1, b)));
    }
    colors.needsUpdate = true;
  }

  /**
   * Apply a crater deformation to the terrain.
   */
  public applyCrater(crater: CraterEvent): void {
    const affected = applyCrater(this.heightmap, crater);

    // Update geometry vertex positions
    const positions = this.geometry.attributes.position as THREE.BufferAttribute;
    for (const idx of affected) {
      positions.setY(idx, this.heightmap[idx]);
    }
    positions.needsUpdate = true;

    // Recompute normals
    this.geometry.computeVertexNormals();

    // Update vertex colors for affected area
    this.updateVertexColors(affected);
  }

  /**
   * Get the terrain height at a world position.
   */
  public getHeightAt(x: number, z: number): number {
    return sampleHeight(this.heightmap, x, z);
  }

  /**
   * Get the terrain normal at a world position.
   */
  public getNormalAt(x: number, z: number): Vec3 {
    return sampleNormal(this.heightmap, x, z);
  }

  /**
   * Regenerate terrain with a new seed (for new rounds).
   */
  public regenerate(seed: number): void {
    this.generateHeightmap(seed);

    const positions = this.geometry.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < positions.count; i++) {
      positions.setY(i, this.heightmap[i]);
    }
    positions.needsUpdate = true;
    this.geometry.computeVertexNormals();
    this.updateVertexColors();
  }

  /**
   * Load a full heightmap (for mid-match join).
   */
  public loadHeightmap(data: Float32Array): void {
    this.heightmap.set(data);

    const positions = this.geometry.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < positions.count; i++) {
      positions.setY(i, this.heightmap[i]);
    }
    positions.needsUpdate = true;
    this.geometry.computeVertexNormals();
    this.updateVertexColors();
  }
}
